import { cx } from '@lastbrand/web-ui-styled-system/css'
import { useEffect } from 'react'

import { PURCHASE_TYPES } from '@src/quince-components/src/atoms/purchaseTypeSelector/PurchaseTypeSelector'
import { fireThirdPartyProductEvents } from '@src/quince-site-common/src/helpers/productHelper'

import BelowBlockSavings from './below-block-savings'
import BestMarketValue from './best-market-value'
import EarnCreditFestivalTag from './earn-credit-festival-tag'
import FinalSaleChip from './final-sale-chip'
import InlineSavings from './inline-savings'
import MainPriceColumn from './main-price-column'
import OfferPriceValue from './offer-price-value'
import PerUnitOfferRow from './per-unit-offer-row'
import { ProductInfoPriceDSProps, usePriceDisplayState } from './price-display-state'
import * as styles from './product-info-price-ds.styles'
import TraditionalPriceColumn from './traditional-price-column'

export type { ProductInfoPriceDSProps } from './price-display-state'

const priceWrapperStylesToApply = {
  festivalOffer: [styles.priceWrapperOfferColumnCss],
  subscriptionOffer: [styles.priceWrapperOfferColumnCss],
  default: [],
}
const priceWrapperStylesToApplyPdpDesignV2 = {
  festivalOffer: [styles.priceWrapperFestivalOfferNewDesignV2Css],
  subscriptionOffer: [styles.priceWrapperSubscriptionOfferNewDesignV2Css],
  default: [],
}

// Orchestrator: derives render flags via usePriceDisplayState and delegates each scenario to a sub-component.
function ProductInfoPriceDS(props: ProductInfoPriceDSProps) {
  const {
    product,
    formattedSubscriptionPrice,
    formattedSalePrice,
    formattedTraditionalPrice,
    savingPercentage,
    formattedFestivalOfferPrice,
    pricePerServing,
    traditionalPricePerServing,
    festivalPricePerUnit,
    subscriptionOfferPricePerUnit,
    isFinalSaleProduct,
    isB2BFinalSaleProduct,
    purchaseOption,
    updatedDesignForV2,
    isVariantThirdParty,
    selectedOptions,
    pdpPriceRedesign = false,
  } = props

  const {
    showNewPdpDesignV2,
    isDiscountLabelPresent,
    hasPerUnitPricing,
    perUnitSavingsPercentage,
    showBestMarketValue,
    showSavingPercentage,
    showPerUnitSavingsPercentage,
    unitForPerServing,
    offerStyleKey,
    earnCreditTagFor3PVendor,
    isSubscriptionOffer,
    isSubscribePurchase,
  } = usePriceDisplayState(props)

  const showOfferPrice = isSubscriptionOffer || !!formattedFestivalOfferPrice

  // Mount-only ([] deps) so the 3P events fire exactly once; kept in the orchestrator, not a leaf.
  useEffect(() => {
    if (isVariantThirdParty && product && selectedOptions) {
      fireThirdPartyProductEvents(product, selectedOptions, !!earnCreditTagFor3PVendor, showBestMarketValue)
    }
  }, [])

  return (
    <div>
      <div
        className={cx(
          styles.priceWrapperCss,
          showNewPdpDesignV2 && styles.priceWrapperNewDesignV2Css,
          ...priceWrapperStylesToApply[offerStyleKey],
          ...priceWrapperStylesToApplyPdpDesignV2[showNewPdpDesignV2 ? offerStyleKey : 'default'],
        )}
      >
        <div
          className={cx(
            showOfferPrice ? styles.priceWrapperMainOfferRowCss : styles.priceWrapperMainCss,
            styles.priceWrapperMainSpaceCss,
          )}
        >
          <MainPriceColumn
            formattedSalePrice={formattedSalePrice}
            formattedTraditionalPrice={formattedTraditionalPrice}
            formattedFestivalOfferPrice={formattedFestivalOfferPrice}
            isSubscriptionOffer={isSubscriptionOffer}
            showNewPdpDesignV2={showNewPdpDesignV2}
            isB2BFinalSaleProduct={isB2BFinalSaleProduct}
            updatedDesignForV2={updatedDesignForV2}
            pdpPriceRedesign={pdpPriceRedesign}
            hasPerUnitPricing={hasPerUnitPricing}
            unitForPerServing={unitForPerServing}
            pricePerServing={pricePerServing}
            traditionalPricePerServing={traditionalPricePerServing}
            earnCreditTagFor3PVendor={earnCreditTagFor3PVendor}
          />
          {showOfferPrice && (
            <OfferPriceValue
              formattedSubscriptionPrice={formattedSubscriptionPrice}
              formattedFestivalOfferPrice={formattedFestivalOfferPrice}
              isSubscriptionOffer={isSubscriptionOffer}
              showNewPdpDesignV2={showNewPdpDesignV2}
              offerStyleKey={offerStyleKey}
            />
          )}
          {earnCreditTagFor3PVendor && formattedFestivalOfferPrice && (
            <EarnCreditFestivalTag
              earnCreditTagFor3PVendor={earnCreditTagFor3PVendor}
              pdpPriceRedesign={pdpPriceRedesign}
            />
          )}
          {isFinalSaleProduct && !isB2BFinalSaleProduct && (
            <FinalSaleChip
              variant="standard"
              showNewPdpDesignV2={showNewPdpDesignV2}
              isVariantThirdParty={isVariantThirdParty}
            />
          )}
          {!showNewPdpDesignV2 && (
            <InlineSavings
              savingPercentage={savingPercentage}
              perUnitSavingsPercentage={perUnitSavingsPercentage}
              showSavingPercentage={showSavingPercentage}
              showPerUnitSavingsPercentage={showPerUnitSavingsPercentage}
              updatedDesignForV2={updatedDesignForV2}
            />
          )}

          {showBestMarketValue && !formattedFestivalOfferPrice && (
            <BestMarketValue pdpPriceRedesign={pdpPriceRedesign} />
          )}
        </div>

        {formattedTraditionalPrice && isDiscountLabelPresent && !hasPerUnitPricing && !showBestMarketValue && (
          <TraditionalPriceColumn
            formattedTraditionalPrice={formattedTraditionalPrice}
            formattedFestivalOfferPrice={formattedFestivalOfferPrice}
            isSubscriptionOffer={isSubscriptionOffer}
            showNewPdpDesignV2={showNewPdpDesignV2}
            pdpPriceRedesign={pdpPriceRedesign}
            updatedDesignForV2={updatedDesignForV2}
          />
        )}
        {showBestMarketValue && formattedFestivalOfferPrice && <BestMarketValue pdpPriceRedesign={pdpPriceRedesign} />}

        {isB2BFinalSaleProduct && !isFinalSaleProduct && (
          <FinalSaleChip variant="b2b" showNewPdpDesignV2={showNewPdpDesignV2} />
        )}
      </div>
      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {showNewPdpDesignV2 && (showSavingPercentage || showPerUnitSavingsPercentage) && (
        <BelowBlockSavings
          savingPercentage={savingPercentage}
          perUnitSavingsPercentage={perUnitSavingsPercentage}
          showSavingPercentage={showSavingPercentage}
        />
      )}

      {/* eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing */}
      {(festivalPricePerUnit || (subscriptionOfferPricePerUnit && purchaseOption === PURCHASE_TYPES.SUBSCRIBE)) &&
        traditionalPricePerServing &&
        unitForPerServing && (
          <PerUnitOfferRow
            festivalPricePerUnit={festivalPricePerUnit}
            subscriptionOfferPricePerUnit={subscriptionOfferPricePerUnit}
            isSubscribePurchase={isSubscribePurchase}
            traditionalPricePerServing={traditionalPricePerServing}
            unitForPerServing={unitForPerServing}
          />
        )}
    </div>
  )
}

export default ProductInfoPriceDS
